"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Linkedin, Mail } from "lucide-react";
const teamMembers = [{
  id: 1,
  name: "Ramin Heydarpour",
  role: "Founder and Managing Partner",
  credential: "As Managing Partner, Ramin provides strategic leadership in innovation, technology commercialization, and business growth. With more than 30 years of global R&D leadership experience, he partners with companies to identify breakthrough opportunities, develop differentiated technologies, and transform innovation into commercially successful products across consumer goods, packaging, advanced materials, and emerging technologies.",
  image: "https://famous-marmoset.10web.cloud/wp-content/uploads/2026/04/RH.jpg?v=dfnz0uga4ig",
  linkedin: "https://www.linkedin.com/in/raminheydarpour/",
  email: "ramin@flexrnd.com"
}, {
  id: 2,
  name: "Ram Santhanam",
  role: "Managing Director of R&D Services",
  credential: "Leads the company's R&D Services, guiding clients to develop market-leading products by combining advanced materials, engineering, intellectual property, and commercialization expertise. His experience spans consumer products, medical devices, automotive, and sustainable packaging, helping organizations accelerate innovation and build lasting competitive advantage.",
  image: "https://famous-marmoset.10web.cloud/wp-content/uploads/2026/05/RS.png?v=4pf19aa6cuv",
  linkedin: "https://www.linkedin.com/in/santhanamram/",
  email: "ram@flexrnd.com"
}, {
  id: 3,
  name: "James Johnson",
  role: "Managing Director of Product Services",
  credential: "Leads Product Services at Flex R&D, helping clients bring innovative products to market through a combination of brand insight, manufacturing expertise, and supply chain leadership. He partners with customers to position products for market success while managing supplier relationships, production, and commercial execution from launch through ongoing supply.",
  image: "https://famous-marmoset.10web.cloud/wp-content/uploads/2026/04/JJ2.jpg?v=vxritkcodm",
  linkedin: "https://www.linkedin.com/in/jamesdjohnson0286/",
  email: "james@flexrnd.com"
}];
function getProfilePage(id: number) {
  if (id === 1) return "RaminHeydarpour";
  if (id === 2) return "RamSanthanam";
  return "JamesJohnson";
}
export default function CoreTeamMembers() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section ref={sectionRef} className="relative bg-[hsl(210,15%,15%)] py-20 px-4 sm:px-6 lg:px-8">
      <div className="absolute inset-0 bg-gradient-to-b from-[hsl(210,15%,15%)] via-[hsl(210,15%,12%)] to-[hsl(210,15%,15%)]" />
      
      <div className="relative mx-auto max-w-7xl">
        <div className="mb-12 text-center">
          <h2 className={`font-display text-4xl font-bold text-white transition-all duration-[250ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>Leadership Team</h2>
          <p className={`mt-4 text-lg text-white/80 transition-all duration-[250ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)] delay-100 ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>Meet the experts driving innovation at Flex R&amp;D</p>
        </div>

        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {teamMembers.map((member, i) => <Card key={member.id} data-index={i} className={`group relative overflow-hidden border border-white/10 bg-white shadow-[0_4px_16px_rgb(0_0_0_/_0.12),0_2px_6px_rgb(0_0_0_/_0.08)] transition-all duration-[250ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-2 hover:shadow-[0_8px_32px_rgb(0_0_0_/_0.16),0_4px_12px_rgb(0_0_0_/_0.08)] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{
          transitionDelay: sectionVisible ? `${i * 100}ms` : "0ms"
        }}>
              <div className="aspect-square w-full overflow-hidden">
                <SafeHydrate><Avatar className="size-full">
                  <AvatarImage src={member.image} alt={member.name} className="object-cover transition-transform duration-[350ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105" />
                  <AvatarFallback className="bg-[hsl(0,0%,95%)] text-4xl">
                    {member.name.split(" ").map(n => n[0]).join("")}
                  </AvatarFallback>
                </Avatar></SafeHydrate>
              </div>

              <CardHeader className="gap-2">
                <CardTitle className="text-xl font-bold text-[hsl(0,0%,0%)]">
                  {member.name}
                </CardTitle>
                <p className="text-sm font-semibold text-[hsl(215,80%,48%)]">
                  {member.role}
                </p>
              </CardHeader>

              <CardContent>
                <p className="text-sm leading-relaxed text-[hsl(0,0%,30%)]">
                  {member.credential}
                </p>
              </CardContent>

              <CardFooter className="flex flex-wrap items-center justify-end gap-4 pt-2">
                <div className="flex items-center gap-4">
                  <Link to={member.linkedin} newTab className="text-[hsl(0,0%,30%)] transition-colors duration-150 hover:text-[hsl(215,80%,48%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(215,80%,48%)]/50 rounded-sm">
                    <Linkedin className="size-5" />
                    <span className="sr-only">LinkedIn profile of {member.name}</span>
                  </Link>
                  <Link to={`mailto:${member.email}`} className="text-[hsl(0,0%,30%)] transition-colors duration-150 hover:text-[hsl(215,80%,48%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(215,80%,48%)]/50 rounded-sm">
                    <Mail className="size-5" />
                    <span className="sr-only">Email {member.name}</span>
                  </Link>
                </div>
              </CardFooter>
            </Card>)}
        </div>
      </div>
    </section>;
}